import * as THREE from 'three'
import TimeManager from '../../Utils/TimeManager'
import {MatrixPositionsEdge, MatrixPositionsCenter, DappCoordinatesWide, DappCoordinatesExpanded} from '../../Utils/MatrixPositions'
import gsap from 'gsap'
import OffchainModel from './Models/OffchainModel'
import ClientModel from './Models/ClientModel'
import BlockchainModel from './Models/BlockchainModel'
import DappModel from './Models/DappModel'
import { Vector3 } from 'three'
import LabelManager from './Labels'
import lineVertexShader from '../../../Shaders/Grid/pulseLineVertex.glsl'
import lineFragmentShader from '../../../Shaders/Grid/pulseLineFragment.glsl'
import TextureManager from './Models/Cubes/TextureManager'

/**
 * The positions for each node are hard coded on Utils/MatrixPositions.js
 * 
 * external_dataset (aka offchain) and customer_dapp (aka clients) - maximum 10 each
 * modify MatrixPositions.js/MatrixPositionsEdge to expand beyond 10
 * 
 * blockchains (aka networks) - maximum 13 
 * modify MatrixPositions.js/MatrixPositionsCenter to expand beyond 13
 * 
 * major_projects (aka dapps) - no limit
 */

export default class NodeLayer
{

    constructor(scene, data, touch) 
    {
        this.textureManager = new TextureManager()
        
        this.allNodes = []

        this.scene = scene

        this.data = data

        this.touch = touch

        this.time = new TimeManager()

        this.scene.time.on('tick', () =>
        {
            this.update()
        })

        this.labels = new LabelManager(scene, data)

        this.createThreeModels()

        this.showWideView()

        var self = this

    }

    showWideView() {
        for (let index = 0; index < this.data.majorDappNodes.length; index++) {
            const thisDappNode = this.data.majorDappNodes[index]
            thisDappNode.threeObject.visible = true
            this.hideLabel(thisDappNode)
            this.hideDownLine(thisDappNode)
            const targetPos = thisDappNode.wideViewPosition
            gsap.to(thisDappNode.threeObject.position, {x: targetPos.x, y: targetPos.y, z: targetPos.z, duration: 0.1, delay: 0, ease: 'power3.inOut'})
            gsap.to(thisDappNode.threeObject.scale, {x: 1, y: 1, z: 1, duration: 0.15, delay: 0, ease: 'power3.inOut'})
        }
        for (let index = 0; index < this.data.clientNodes.length; index++) {
            this.showNode(this.data.clientNodes[index])
            const targetPos = this.data.clientNodes[index].wideViewPosition
            gsap.to(this.data.clientNodes[index].threeObject.position, {x: targetPos.x, y: targetPos.y, z: targetPos.z, duration: 0.1, delay: 0, ease: 'power3.inOut'})
        }
        for (let index = 0; index < this.data.blockChainNodes.length; index++) {
            this.showNode(this.data.blockChainNodes[index])
            const targetPos = this.data.blockChainNodes[index].wideViewPosition
            gsap.to(this.data.blockChainNodes[index].threeObject.position, {x: targetPos.x, y: targetPos.y, z: targetPos.z, duration: 0.1, delay: 0, ease: 'power3.inOut'})
        }
        for (let index = 0; index < this.data.offchainNodes.length; index++) {
            this.showNode(this.data.offchainNodes[index])
            const worldXYZ = this.data.offchainNodes[index].wideViewPosition
            this.data.offchainNodes[index].threeObject.position.set(worldXYZ.x, worldXYZ.y, worldXYZ.z)
        }
    }

    showExpandedView(node) {
        this.showNode(node)
        for (let index = 0; index < this.data.majorDappNodes.length; index++) {
            const thisDappNode = this.data.majorDappNodes[index]
            if (node.myParsedIndex == thisDappNode.parentBlockchainIndex) {
                const targetPos = thisDappNode.expandedViewPosition
                gsap.to(thisDappNode.threeObject.position, {x: targetPos.x, y: targetPos.y, z: targetPos.z, duration: 0.1, delay: 0, ease: 'power3.inOut'})
                gsap.to(thisDappNode.threeObject.scale, {x: 1.6, y: 1.6, z: 1.6, duration: 0.15, delay: 0, ease: 'power3.inOut'})
                this.showNode(thisDappNode)
                this.showDownLine(thisDappNode)
            }
        }
        for (let index = 0; index < this.data.clientNodes.length; index++) {
            const thisClientNode = this.data.clientNodes[index]
            if (node.myParsedIndex == thisClientNode.parentBlockchainIndex) {
                const worldXYZ = thisClientNode.expandedViewPosition
                thisClientNode.threeObject.position.set(worldXYZ.x, worldXYZ.y, worldXYZ.z)
                this.showNode(thisClientNode)
            }
        }

        const targetPos = node.expandedViewPosition
        gsap.to(node.threeObject.position, {x: targetPos.x, y: targetPos.y, z: targetPos.z, duration: 0.1, delay: 0, ease: 'power3.inOut'})
    }

    createThreeModels() {
        this.gridSquareWidth = this.scene.STside / this.scene.GRIDsize
        this.cubeSize = this.gridSquareWidth
        this.xZero = (-this.scene.STside + this.gridSquareWidth) * 0.5
        this.zZero = (this.scene.STside - this.gridSquareWidth) * 0.5
        this.xMaximum = (-this.scene.STside + this.gridSquareWidth) * 0.5
        this.zMaximum = (this.scene.STside - this.gridSquareWidth) * 0.5
        this.yPosition = this.scene.GRIDposition + this.cubeSize * 0.5

        this.createOffchainModels()

        this.createClientModels()

        this.createBlockchainModels()

        this.createDappModels()
    }

    hideAllNodes() {
        for (let index = 0; index < this.data.clientNodes.length; index++) {
            this.hideNode(this.data.clientNodes[index])
        }
        for (let index = 0; index < this.data.blockChainNodes.length; index++) {
            this.hideNode(this.data.blockChainNodes[index])
        }
        for (let index = 0; index < this.data.offchainNodes.length; index++) {
            this.hideNode(this.data.offchainNodes[index])
        }
    }

    hideAllLabels() {
        for (let index = 0; index < this.data.majorDappNodes.length; index++) {
            this.hideLabel(this.data.majorDappNodes[index])
        }
        for (let index = 0; index < this.data.clientNodes.length; index++) {
            this.hideLabel(this.data.clientNodes[index])
        }
        for (let index = 0; index < this.data.blockChainNodes.length; index++) {
            this.hideLabel(this.data.blockChainNodes[index])
        }
        for (let index = 0; index < this.data.offchainNodes.length; index++) {
            this.hideLabel(this.data.offchainNodes[index])
        }
    }

    hideNode(node) {
        this.scene.remove(node.threeObject)
        this.hideLabel(node)
        this.removeFromTouch(node)
    }

    showNode(node) {
        if (!node.threeObject.parent) this.scene.add(node.threeObject)
        this.showLabel(node)
        this.addToTouch(node)
    }

    addToTouch(node) {
        this.touch.addToRaycast(node)
    }

    removeFromTouch(node) {
        this.touch.removeFromRaycast(node)
    }

    hideLabel(node) {
        node.htmllabel.element.classList.remove('visible')
        node.lineObject.visible = false
    }

    showLabel(node) {
        node.htmllabel.element.classList.add('visible')
        node.lineObject.visible = true
    }

    showDownLine(node) {
        for (let i = 0; i < node.downLineObject.length; i++) {
            node.downLineObject[i].visible = true
        }
    }

    hideDownLine(node) {
        for (let i = 0; i < node.downLineObject.length; i++) {
            node.downLineObject[i].visible = false
        }
    }

    createOffchainModels() {
        for (let index = 0; index < this.data.offchainNodes.length; index++) {

            const threeObj = OffchainModel(this.gridSquareWidth, this.data.offchainNodes[index], this.textureManager)

            const positionObj = this.fetchNodePosition(0, index)

            this.data.offchainNodes[index].wideViewPosition = positionObj

            this.data.offchainNodes[index].expandedViewPosition = positionObj

            const gridCoords = this.fetchNodeCoordinates(0, this.data.offchainNodes.length, index)

            this.data.offchainNodes[index].gridCoords = gridCoords
            
            this.scene.add( threeObj );

            this.labels.createLabel(this.data.offchainNodes[index])

            this.createDownLine(this.data.offchainNodes[index])

            this.allNodes.push(this.data.offchainNodes[index])
        }
    }

    createClientModels() {
        for (let index = 0; index < this.data.clientNodes.length; index++) {

            const threeObj = ClientModel(this.gridSquareWidth, this.data.clientNodes[index], this.textureManager)

            const positionObj = this.fetchNodePosition(1, index)

            this.data.clientNodes[index].wideViewPosition = positionObj

            this.data.clientNodes[index].expandedViewPosition = positionObj

            const gridCoords = this.fetchNodeCoordinates(1, this.data.clientNodes.length, index)

            this.data.clientNodes[index].gridCoords = gridCoords

            this.scene.add( threeObj );

            this.labels.createLabel(this.data.clientNodes[index])

            this.createDownLine(this.data.clientNodes[index])

            this.allNodes.push(this.data.clientNodes[index])
        }
    }

    createBlockchainModels() {
        for (let index = 0; index < this.data.blockChainNodes.length; index++) {

            const threeObj = BlockchainModel(this.gridSquareWidth, this.data.blockChainNodes[index], this.textureManager)

            const positionObj = this.fetchNodePosition(2, index)

            this.data.blockChainNodes[index].wideViewPosition = positionObj

            this.data.blockChainNodes[index].expandedViewPosition = new Vector3(0, this.yPosition, 0)

            const gridCoords = this.fetchNodeCoordinates(2, this.data.blockChainNodes.length, index)

            this.data.blockChainNodes[index].gridCoords = gridCoords

            this.scene.add( threeObj );

            this.labels.createLabel(this.data.blockChainNodes[index])

            this.createDownLine(this.data.blockChainNodes[index])

            this.allNodes.push(this.data.blockChainNodes[index])
        }
    }

    createDappModels() {
        for (let index = 0; index < this.data.majorDappNodes.length; index++) {

            const threeObj = DappModel(this.gridSquareWidth, this.data.majorDappNodes[index], this.textureManager)

            const blockchainIndex = this.data.majorDappNodes[index].parentBlockchainIndex
            const myBlockchain =  this.data.blockChainNodes[blockchainIndex]

            //add to scene
            this.data.blockChainNodes[blockchainIndex].threeObject.add(threeObj)

            const myDappIndex = this.data.majorDappNodes[index].dappChildIndex

            this.data.majorDappNodes[index].wideViewPosition = this.dappPositionWide(myDappIndex)

            this.data.majorDappNodes[index].expandedViewPosition = this.dappPositionExpanded(myBlockchain.numberOfDapps, myDappIndex) 

            this.labels.createLabel(this.data.majorDappNodes[index])

            this.createDownLine(this.data.majorDappNodes[index])

            this.allNodes.push(this.data.majorDappNodes[index])
        }
    }

    dappPositionWide(index) {
        let truncIndex = index
        let layerIndex = 0
        let matrixXYZ = new Vector3()
        let worldXYZ = new Vector3()
        while (truncIndex > 7) {
            truncIndex -= 8
            layerIndex++
        }
        matrixXYZ.x = DappCoordinatesWide[truncIndex][0]
        matrixXYZ.z = DappCoordinatesWide[truncIndex][1]
        matrixXYZ.y = -layerIndex
        const miniCubeSize = this.gridSquareWidth*0.9 * 0.25
        worldXYZ.z = matrixXYZ.x * this.gridSquareWidth*0.9 * 0.5 + -matrixXYZ.x * miniCubeSize * 0.5
        worldXYZ.x = -(matrixXYZ.z * this.gridSquareWidth*0.9 * 0.5 + -matrixXYZ.z * miniCubeSize * 0.5)
        worldXYZ.y = -this.gridSquareWidth*0.9 * 0.9 + (miniCubeSize * 0.5 + this.cubeSize * 0.4) * matrixXYZ.y
        return worldXYZ
    }

    dappPositionExpanded(totalNodes, index) {
        const totalNodesTrunc = Math.min(18, totalNodes)
        let worldXYZ = new Vector3()
        const matrixXZ = DappCoordinatesExpanded[totalNodesTrunc][index]
        worldXYZ.x = -matrixXZ[0] * this.gridSquareWidth * 2
        worldXYZ.z = matrixXZ[1] * this.gridSquareWidth * 2
        worldXYZ.y = 0 - this.gridSquareWidth * 0.5 + this.gridSquareWidth * 0.25
        return worldXYZ
    }

    fetchNodeCoordinates(type, totalNumber, index) {
        const recenteringConstant = Math.round(this.scene.GRIDsize - 13) * 0.5
        if (type == 0) {//offchains
            const totalIndex = Math.min(totalNumber, MatrixPositionsEdge.length)
            let coordsTemp = [0,0]
            coordsTemp[0] = MatrixPositionsEdge[totalIndex][index][0] + recenteringConstant
            coordsTemp[1] = MatrixPositionsEdge[totalIndex][index][1] + recenteringConstant
            return coordsTemp
        }
        if (type == 1) {//clients
            const totalIndex = Math.min(totalNumber, MatrixPositionsEdge.length)
            let coordsTemp = [0,0]
            coordsTemp[0] = MatrixPositionsEdge[totalIndex][index][1] + recenteringConstant
            coordsTemp[1] = MatrixPositionsEdge[totalIndex][index][0] + recenteringConstant
            return coordsTemp
        }
        if (type == 2) {//blockchains
            const totalIndex = Math.min(totalNumber, MatrixPositionsCenter.length)
            let coordsTemp = [0,0]
            coordsTemp[0] = MatrixPositionsCenter[totalIndex][index][1] + recenteringConstant
            coordsTemp[1] = MatrixPositionsCenter[totalIndex][index][0] + recenteringConstant
            return coordsTemp
        }
    }

    fetchNodePosition(type, index) {
        let xzCoordinates = null
        if (type == 0) { //offchains
            xzCoordinates = this.fetchNodeCoordinates(type, this.data.offchainNodes.length, index)
        }
        if (type == 1) { //clients
            xzCoordinates = this.fetchNodeCoordinates(type, this.data.clientNodes.length, index)
        }
        if (type == 2) { //blockchains
            xzCoordinates = this.fetchNodeCoordinates(type, this.data.blockChainNodes.length, index)
        }
        let worldXYZ = new Vector3()
        worldXYZ.x = this.xZero + xzCoordinates[0] * this.gridSquareWidth
        worldXYZ.z = this.zZero - xzCoordinates[1] * this.gridSquareWidth
        worldXYZ.y = this.yPosition
        return worldXYZ
    }

    createDownLine(node){
        const numLines = 1 + node.numberOfDapps + 1.5*Math.random()
        for (let index = 0; index < numLines; index++) {
            const lineLength = 2
            const lineMaterial = new THREE.ShaderMaterial({
                vertexShader: lineVertexShader,
                fragmentShader: lineFragmentShader,
                wireframe: false,
                transparent: true,
                depthWrite: false,
                blending: THREE.AdditiveBlending,
                uniforms:
                {
                    uTime: { value: 0 },
                    uOffset: { value: Math.random()*1.0 },
                },
            })
            const lineGeometry = new THREE.PlaneGeometry(lineLength, 0.04 + 0.03*Math.random(), 1, 1)
            const line = new THREE.Mesh(lineGeometry, lineMaterial)
            line.translateY(-lineLength*0.5)
            line.rotateZ(-Math.PI*0.5)
            node.threeObject.add(line)
            node.downLineObject.push(line)
        }
    }

    update() {
        for (let index = 0; index < this.allNodes.length; index++) {
            const element = this.allNodes[index];
            let lineWorldPosition = new THREE.Vector3()
            element.downLineObject[0].getWorldPosition(lineWorldPosition)
            const lookTarget = new THREE.Vector3(this.scene.camera.position.x, lineWorldPosition.y, this.scene.camera.position.z)
            for (let j = 0; j < element.downLineObject.length; j++) {
                element.downLineObject[j].material.uniforms.uTime.value = this.time.elapsed
                element.downLineObject[j].lookAt(lookTarget)
                element.downLineObject[j].rotateZ(-Math.PI*0.5)
            }
        }
    }



}